import { createApi } from '@reduxjs/toolkit/query/react';

import { isMockCaseEnabled } from '#shared';

import {
  fetchWithAuthBaseQuery,
  validateAndTransformResponse,
} from '#shared/api';
import type { DataWithValidation } from '#shared/api';

import { asCaseTrackingResult, caseTrackingPath } from './caseTrackingPayload';
import { loadDemoTrackingSource } from './demoSource';
import { caseTrackingResponseSchema } from './schemas';

import type { CaseTrackingResponse } from '../model';

// One case's plan legs merged with their live tracking, fetched ON DEMAND
// for the case the operator has selected (ADR ARCH-014).
//
// **A separate `createApi` from `activeCasesApi`.** That one is a fleet-wide poll
// whose cache entry is `void`; this one is keyed per case and served `no-store` by
// the BE. Sharing a reducer path would make "which shape is this entry" a runtime
// question.
//
// `keepUnusedDataFor: 0` — the payload is a live position. Re-selecting a
// case a minute later must re-read it, not restore a stale one.

export interface CaseTrackingArgs {
  caseId: string;
}

const transform = (response: unknown) =>
  validateAndTransformResponse(
    caseTrackingResponseSchema,
    asCaseTrackingResult(response),
    value => value?.caseId,
    'caseTracking',
  );

export const caseTrackingApi = createApi({
  reducerPath: 'caseTrackingApi',
  baseQuery: fetchWithAuthBaseQuery,
  refetchOnReconnect: true,
  keepUnusedDataFor: 0,
  endpoints: build => ({
    getCaseTracking: build.query<
      DataWithValidation<CaseTrackingResponse>,
      CaseTrackingArgs
    >({
      // `queryFn`, not `query` (E038/E039) — see `activeCasesApi.getActiveCases`.
      queryFn: async ({ caseId }, _api, _extra, baseQuery) => {
        const buildDemo = isMockCaseEnabled()
          ? await loadDemoTrackingSource()
          : null;
        if (buildDemo) {
          return { data: transform(buildDemo(Date.now())) };
        }

        const result = await baseQuery(caseTrackingPath(caseId));

        return result.error
          ? { error: result.error }
          : { data: transform(result.data) };
      },
    }),
  }),
});

export const { useGetCaseTrackingQuery } = caseTrackingApi;
